import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Database, Settings, Zap, CheckCircle, ArrowRight, ArrowDown } from "lucide-react";
import { NichePage } from "./NichePageTemplate";

const workflowSteps = [
  { label: "Lead", desc: "New inquiry comes in" },
  { label: "Follow-Up", desc: "Timely outreach & qualification" },
  { label: "Appointment", desc: "Scheduled estimate or consultation" },
  { label: "Job", desc: "Work performed on-site" },
  { label: "Customer", desc: "Satisfied client on record" },
  { label: "Follow-Up", desc: "Review request & repeat business" },
];

const sections = [
  {
    icon: Users,
    name: "Lead & Customer Management",
    items: [
      "Lead management", "Customer operations", "Follow-up",
      "Administrative coordination"
    ]
  },
  {
    icon: Settings,
    name: "Scheduling & Job Operations",
    items: [
      "Scheduling workflows", "Job operations", "Appointment coordination"
    ]
  },
  {
    icon: Database,
    name: "CRM",
    items: [
      "CRM organization", "Pipeline management", "Database management",
      "Follow-up systems"
    ]
  },
  {
    icon: Zap,
    name: "Automation",
    items: [
      "Workflow automation", "Notifications", "Task automation",
      "Lead routing", "Integrations"
    ]
  }
];

export const HomeFieldServicesPage = () => (
  <div>
    {/* Hero */}
    <section className="py-20 bg-slate-50 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <h1 className="text-4xl sm:text-5xl text-[#2D4A6D] mb-6 font-serif">Home & Field Services</h1>
        <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Operational systems connecting leads, customers, schedules, and field work.
        </p>
      </div>
    </section>

    {/* Workflow Diagram */}
    <section className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl text-[#2D4A6D] mb-4 font-serif">The Workflow</h2>
          <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
          <p className="text-slate-600 max-w-xl mx-auto">
            Every home and field service business follows this cycle. The right systems keep it moving without gaps.
          </p>
        </div>

        {/* Desktop: Horizontal flow */}
        <div className="hidden md:flex items-start justify-between gap-2">
          {workflowSteps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex items-center"
            >
              <div className="text-center w-[130px]">
                <div className="w-16 h-16 bg-[#2D4A6D] flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-xs font-bold uppercase tracking-wide leading-tight">{step.label}</span>
                </div>
                <p className="text-xs text-slate-500 leading-snug">{step.desc}</p>
              </div>
              {i < workflowSteps.length - 1 && (
                <ArrowRight className="w-5 h-5 text-[#2D4A6D]/40 flex-shrink-0 -mt-4" />
              )}
            </motion.div>
          ))}
        </div>

        {/* Mobile: Vertical flow */}
        <div className="md:hidden space-y-3">
          {workflowSteps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <div className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200">
                <div className="w-14 h-14 bg-[#2D4A6D] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold uppercase tracking-wide leading-tight text-center">{step.label}</span>
                </div>
                <p className="text-sm text-slate-600">{step.desc}</p>
              </div>
              {i < workflowSteps.length - 1 && (
                <div className="flex justify-center py-1">
                  <ArrowDown className="w-5 h-5 text-[#2D4A6D]/40" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Capability Sections */}
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {sections.map((sec, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <div className="p-8 border border-slate-200 hover:border-[#2D4A6D]/50 transition-colors bg-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#2D4A6D] flex items-center justify-center flex-shrink-0">
                    <sec.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2D4A6D]">{sec.name}</h3>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {sec.items.map((item, j) => (
                    <span key={j} className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-3 py-1">
                      <CheckCircle className="w-3 h-3 text-[#2D4A6D]" />{item}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-[#2D4A6D] text-white">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-3xl sm:text-4xl mb-6 font-serif">Discuss Requirements</h2>
        <div className="w-16 h-0.5 bg-white/30 mx-auto mb-8" />
        <p className="text-slate-300 mb-10">
          Schedule a strategy call to identify operational requirements and determine the appropriate engagement structure.
        </p>
        <Link to="/book">
          <button className="bg-white text-[#2D4A6D] hover:bg-slate-100 px-10 py-4 font-medium flex items-center mx-auto">
            Book a Strategy Call <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </Link>
      </div>
    </section>
  </div>
);
